import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { UpdateSettingsBody } from '@contracts/settings';
import {
  ACCOUNT_EXPORT_URL,
  useDeleteAccount,
  useMe,
  useOrg,
  useSettings,
  useUpdateSettings,
} from '../api/queries';
import { Broken, Button, Card, Field, Modal, Picker, SkeletonRows, useNotices } from '../ui';
import { SettingsLayout } from '../settings/SettingsLayout';
import { WRITTEN_IN } from '../lib/written-in';
import { CalendarCard } from '../settings/CalendarCard';

const LANGUAGES: Array<[string, string]> = [
  ['auto', 'Whatever is spoken'],
  ['multi', 'Mixed — several in one call'],
  ['en', 'English'],
  ['ru', 'Russian'],
  ['de', 'German'],
  ['fr', 'French'],
  ['es', 'Spanish'],
  ['pl', 'Polish'],
];

/**
 * Board 15 — four ordinary settings, each a live control: it takes effect for
 * the next meeting the moment it changes. A series can override the first
 * three; the workspace's retention sits underneath the fourth.
 */
export function Settings(): ReactElement {
  const me = useMe();
  const org = useOrg();
  const settings = useSettings();
  const update = useUpdateSettings();
  const { show } = useNotices();
  const [name, setName] = useState('');
  useEffect(() => setName(settings.data?.botDisplayName ?? ''), [settings.data?.botDisplayName]);

  const save = (body: UpdateSettingsBody, text = 'Saved — the next meeting runs this way.') =>
    update.mutate(body, {
      onSuccess: () => show({ tone: 'success', text }),
      onError: (e) => show({ tone: 'failure', text: e.message }),
    });

  return (
    <SettingsLayout title="Your settings" sub="four ordinary settings — a series can override each">
      <CalendarCard />
      {settings.isPending ? (
        <SkeletonRows rows={4} />
      ) : settings.isError ? (
        <Card flush>
          <Broken what="Your settings" retry={() => void settings.refetch()} />
        </Card>
      ) : (
        <Card title="Recording" flush>
          <div className="setting">
            <span className="k">Your notetaker is called</span>
            <div className="v">
              <Field
                aria-label="Your notetaker is called"
                placeholder={`Recording for ${me.data?.name ?? me.data?.email ?? 'you'}`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => {
                  const next = name.trim() || null;
                  if (next !== settings.data.botDisplayName) save({ botDisplayName: next });
                }}
                onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                style={{ width: 260 }}
              />
              <span className="why">
                what the other people in the call see — left empty, it says that it is recording and
                for whom. Whatever it is called, it also says so in the call’s chat when it joins.
              </span>
            </div>
          </div>
          <div className="setting">
            <span className="k">Join my meetings</span>
            <div className="v">
              <Picker
                aria-label="Join my meetings"
                value={
                  !settings.data.recordEverything
                    ? 'chosen'
                    : settings.data.recordOneToOnes
                      ? 'all'
                      : 'not-ones'
                }
                onChange={(v) =>
                  v === 'chosen'
                    ? save(
                        { recordEverything: false },
                        'From now on the notetaker comes only to the meetings you turn on.',
                      )
                    : save(
                        { recordEverything: true, recordOneToOnes: v === 'all' },
                        v === 'all'
                          ? 'Everything with a link is recorded from now on.'
                          : 'Everything but one-to-ones is recorded from now on.',
                      )
                }
                width={260}
                options={[
                  { value: 'all', label: 'Everything with a link' },
                  { value: 'not-ones', label: 'Everything but one-to-ones' },
                  { value: 'chosen', label: 'Only meetings I turn on' },
                ]}
              />
              <span className="why">
                {settings.data.recordEverything
                  ? 'one-to-ones are recorded unless you say otherwise; three or more always is'
                  : 'turn a meeting on with Record this one, or a whole series on its page'}
              </span>
            </div>
          </div>
          <div className="setting">
            <span className="k">Transcription language</span>
            <div className="v">
              <Picker
                aria-label="Transcription language"
                value={settings.data.language}
                onChange={(language) => save({ language })}
                width={260}
                options={LANGUAGES.map(([value, label]) => ({ value, label }))}
              />
              <span className="why">a series can override this</span>
            </div>
          </div>
          {/* ⚠ Spoken and written are two different questions, and this is the
              second one (Vladimir, 2026-09-21: "I want a setting on what
              language to write meeting notes/commitment and knowledge").
              Notes and commitments are each person's own and a series can
              override either. Knowledge is NOT here: it is read across the
              whole workspace for years, by people who did not record the
              meeting it came from, so it belongs to the workspace and lives on
              the Organisation page (Vladimir, 2026-09-22). */}
          {(
            [
              [
                'noteLanguage',
                'Notes written in',
                'the note and its title; a series can override this',
              ],
              [
                'ledgerLanguage',
                'Commitments written in',
                'what was promised and decided; a series can override this',
              ],
            ] as const
          ).map(([key, label, why]) => (
            <div className="setting" key={key}>
              <span className="k">{label}</span>
              <div className="v">
                <Picker
                  aria-label={label}
                  value={settings.data[key]}
                  onChange={(code) => save({ [key]: code })}
                  width={260}
                  options={WRITTEN_IN.map(([value, name]) => ({ value, label: name }))}
                />
                <span className="why">{why}</span>
              </div>
            </div>
          ))}
          <div className="setting">
            <span className="k">Keeping</span>
            <div className="v">
              <span className="t-ui">
                {org.data?.transcriptRetentionDays
                  ? `Transcripts removed after ${org.data.transcriptRetentionDays} days`
                  : 'Transcripts kept as long as the meeting'}{' '}
                · video deleted after{' '}
                {org.data ? `${org.data.videoRetentionDays} days` : 'the workspace’s period'}
              </span>
              <span className="why">
                <Link to="/settings/organisation/defaults">the organisation sets the days</Link>; a
                series can keep no video at all
              </span>
            </div>
          </div>
        </Card>
      )}
      <YourData peopleCounted={org.data?.peopleAnalytics ?? false} orgName={org.data?.name} />
    </SettingsLayout>
  );
}

/**
 * The two rights a person exercises alone (GDPR Art. 15/20 and 17): take
 * everything, and end it. Plain words, because this is the card somebody opens
 * on the day they are worried.
 */
function YourData({
  peopleCounted,
  orgName,
}: {
  peopleCounted: boolean;
  orgName: string | undefined;
}): ReactElement {
  const me = useMe();
  const remove = useDeleteAccount();
  const { show } = useNotices();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const email = me.data?.email ?? '';
  const close = () => {
    setOpen(false);
    setTyped('');
  };
  return (
    <Card title="Your data" flush>
      <div className="setting">
        <span className="k">Take a copy</span>
        <div className="v">
          {/* A link, not a fetch: the browser streams it straight to disk. */}
          <a className="btn btn-secondary" href={ACCOUNT_EXPORT_URL} download>
            Download everything
          </a>
          <span className="why">
            one JSON file: your account, and every meeting you can open — its note, its commitments
            and decisions, and its transcript where you may read it. Video is not in the file.
          </span>
        </div>
      </div>
      {peopleCounted ? (
        <div className="setting">
          <span className="k">What your organisation counts</span>
          <div className="v">
            <span className="t-ui">{orgName ?? 'Your organisation'} counts its people by name</span>
            <span className="why">
              its administrators see how many meetings you were in, your time in calls, and your
              open and done commitments. Never what anybody said.
            </span>
          </div>
        </div>
      ) : null}
      <div className="setting">
        <span className="k">Delete your account</span>
        <div className="v">
          <Button variant="danger" onClick={() => setOpen(true)}>
            Delete my account…
          </Button>
          <span className="why">
            your account, your own space, and every meeting your notetaker recorded into it — for
            everyone it was shared with
          </span>
        </div>
      </div>
      {open ? (
        <Modal
          title="Delete your account"
          onClose={close}
          foot={
            <>
              <Button onClick={close}>Keep it</Button>
              <Button
                variant="danger"
                disabled={!email || typed.trim().toLowerCase() !== email.toLowerCase()}
                working={remove.isPending ? 'Deleting…' : false}
                onClick={() =>
                  remove.mutate(undefined, {
                    onSuccess: () => window.location.assign('/start'),
                    onError: (err) => {
                      // A 409 is the server saying what has to happen first —
                      // its sentence is the message.
                      show({ tone: 'failure', text: err.message });
                      close();
                    },
                  })
                }
              >
                Delete my account
              </Button>
            </>
          }
        >
          <p className="t-body">
            This deletes your account, your calendar connection, and every meeting in your own space
            — note, transcript and video — including for the people you shared them with. Meetings
            in your organisation’s shared spaces stay, with your name in them as a participant. It
            cannot be undone; backups expire within 30 days. If you want a copy, download it first.
          </p>
          <Field
            aria-label="Type your email address to confirm"
            placeholder={`Type ${email} to confirm`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            style={{ marginTop: 12, width: '100%' }}
          />
        </Modal>
      ) : null}
    </Card>
  );
}
